/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
*
* View to swap the Live Widget Visualization through the ODT
* Options
* {
*    showTitles: boolean,  // Whether to show the titles for the available/recommended visualizations
*    visualizations: {
*      recommended: [],     // Array of visualizations to place in recommended section
*      other: []           // Array of visualizations to put in other section
*    },
*    currentVis: String,   // The id of the currently selected visualization
*    widget: Live Widget   // The widget that is creating the vischanger view
* }
*
**/

define([
	'../../../lib/@waca/core-client/js/core-client/ui/core/View',
	'../../../lib/@waca/core-client/js/core-client/utils/Utils',
	'../nls/StringResources',
	'jquery',
	'underscore',
	'react-dom',
	'react',
	'ca-ui-toolkit',
	'dashboard-analytics/DynamicFileLoader',
	'gemini/app/util/ErrorUtils'
], function(BaseView, Utils, resources, $, _, ReactDOM, React, Toolkit, DynamicFileLoader, ErrorUtils) {
	/* eslint react/prop-types: 0 */ // TODO: Remove once we have prop-types brought in from glass.
	const { Accordion } = Toolkit;
	const { AccordionItem } = Toolkit;
	const { SVGIcon } = Toolkit;
	const { Label } = Toolkit;
	const { SVGIconDecoration } = Toolkit;
	const { ProgressIndicator } = Toolkit;

	// Limit the number of recommended visualizations to show in the recommended section.
	const MaxRecommendedVisualizationsToShow = 6;
	const AUTO_ID = 'auto';

	/******************************************************
	 * Recommended Accordion Section
	 *	AccordionItem
	 *		[PropItems] - SVG (with possible deco) and Label
	 * All visualizations Accordion Section
	 * 	AccordionItem
	 *		[PropItems] - SVG (with possible deco) and Label
	********************************************************/
	/*
	* Class to render the react flyout components
	*/
	class ReactComponents extends React.Component {
		/*
		* The props hold a state that should be used as this classes state.
		* When the state changes the component gets re-rendered.
		*/
		constructor(props) {
			super(props);
			this.state = { ...props.state };
			this.state.checkMarkIcon = props.state.iconsFeature.getIcon('CheckmarkSVG');
		}

		/**
		* @param {string} id of the selected visualization
		* @return {React Component} the decoration if the specified id matched the selected id.
		*/
		getSelectedDecoration(id) {
			// The decoration is basically a checkmark to show the selected vis.
			if (this.state.selectedId === id) {
				return <SVGIconDecoration
					iconId={this.state.checkMarkIcon.id}
					location={'bottomRight'}
					style={{ right:'-4px' }}/>;
			}
		}

		/**
		* @param {object} item describing all the info needed to create a prop item
		* @param {String} dataType either 'Recommended' or 'Other'
		* @return {React Object}
		*/
		renderPropItem(item, dataType) {
			const decoration = this.getSelectedDecoration(item.id);
			const pressed = (this.state.selectedId === item.id)?'true':'false';
			return (
				<div role={'group'} className='prop-item'>
					<div tabIndex={0}
						data-type={dataType}
						className='prop-icon vis'
						role={'button'}
						title={item.name}
						data-id={item.id}
						appcues-data-id={item.id}
						aria-label={item.name}
						aria-pressed={pressed}>
						<SVGIcon
							iconId={this.state.svgsMap[item.id].id}
							height={42}
							width={42}>
							{decoration}
						</SVGIcon>
						<Label className='prop-icon-label' label={item.name}/>
					</div>
				</div>
			);
		}

		/**
		* @param {Array} items all the visualizations to add to the row
		* @param {Boolean} isRecommended true iff the Vis Row is the recommended row.
		* @returns {Array} of React components for each Vis in the Vis Row.
		*/
		loadVisRowItems(items, isRecommended) {
			let svgs = [];
			const dataType = isRecommended ? 'Recommended' : 'Other';
			items.forEach((item) => {
				if (item.id && this.state.svgsMap[item.id]) {
					svgs.push(this.renderPropItem(item, dataType));
				}
			});

			return svgs;
		}

		/**
		* Render the progress indicator to show we are loading.
		*/
		_renderLoading() {
			return (
				<div className='vis-changer-progressIndicator'>
					<ProgressIndicator size={'large'} variant={'circle'} />
				</div>
			);
		}

		_getVisRowItems(options) {
			let { items, isRecommended } = options;
			const loadedSvgs = this.loadVisRowItems(items, isRecommended);
			return (
				<div className='visChangerRow'>
					{loadedSvgs}
				</div>
			);
		}

		_getRecommendedAccordionItem() {
			const recommendedOptions = {
				items: this.state.recommended.items,
				isRecommended: true
			};
			return this.state.recommended.isLoading ? this._renderLoading() : this._getVisRowItems(recommendedOptions);
		}

		_getAllVisAccordionItem() {
			const allVisOptions = {
				items: this.state.all.items,
				isRecommended: false
			};
			return this.state.all.isLoading ? this._renderLoading() : this._getVisRowItems(allVisOptions);
		}

		/**
		* Render the collapsible sections (accordions)
		* @return {React Object} representing the Accordion and its sections.
		*/
		_renderAccordions() {
			let recommendedItem = this._getRecommendedAccordionItem();
			const isConsumer = this.state.isConsumer;
			if (!isConsumer) {
				return (
					<Accordion>
						<AccordionItem itemName={resources.get('recommended_visualizations')} icon='left' open={true}>
							{recommendedItem}
						</AccordionItem>
						<AccordionItem itemName={resources.get('all_visualizations')} icon='left' open={false} onChange={this.state.onItemChange}>
							{this._getAllVisAccordionItem()}
						</AccordionItem>
					</Accordion>
				);
			} else {
				return (
					<Accordion>
						<AccordionItem itemName={resources.get('recommended_visualizations')} icon='left' open={true}>
							{recommendedItem}
						</AccordionItem>
					</Accordion>
				);
			}
		}

		/**
		* @returns {React Object} progress indicator if we are loading, accordions otherwise.
		*/
		render() {
			return this._renderAccordions();
		}
	}

	/**
	* The following view is the actual ODT Vis Changer view. It will handle
	* selection of the react components to change vis type, rendering the different
	* choices into two collapsible sections as well as showing a loading indicator
	* until all the SVGs are loaded.
	*/
	var View = BaseView.extend({

		events: {
			'primaryaction .vis' : '_selectVis'
		},

		/**
		* Load the SVG files representing all the visualizations
		*/
		_loadSVGFileMap() {
			// If we've already loaded the SVG files, return the map
			if (this.viewState.svgsMap && _.keys(this.viewState.svgsMap).length > 0) {
				return Promise.resolve(this.viewState.svgsMap);
			} else {
				this.viewState.svgsMap = {};
				this._getAllAvailableVisualizations();
				if(this.viewState.all && this.viewState.all.items){
					const iconsFeature = this.viewState.iconsFeature;
					this.viewState.all.items.forEach(item => {
						const visIcon = iconsFeature.getIcon(item.visId);
						this.viewState.svgsMap[item.visId] = visIcon;
					});
					if(!Object.keys(this.viewState.svgsMap).includes('auto')){
						const autoIcon = iconsFeature.getIcon('auto');
						this.viewState.svgsMap['auto'] = autoIcon;
					}
					return Promise.resolve(this.viewState.svgsMap);
				}
			}
		},

		init: function(options) {
			// Provide the basic element.
			this.el = $('<div class=\'visChangerContainer\'/>');
			View.inherited('init', this, arguments);

			this.applyAction = options && options.actions && options.actions.apply;
			let finalOptions = options && options.state ? options.state : options;
			_.extend(this, finalOptions || {});
			this.iconsFeature = this.dashboard.getFeature('Icons');
			this._initializeViewState();
		},

		_elementExists: function() {
			let hasLength = false,
				hasKids = false,
				el = this.el;
			if (el) {
				if (el.length) {
					hasLength = el.length > 0;
				}
				if (el.children) {
					hasKids = el.children !== 0;
				}
			}
			return el && (hasKids || hasLength);
		},

		remove: function() {
			if (this._elementExists()) {
				ReactDOM.unmountComponentAtNode(this.el);
			}
			this.viewState = null;
			return View.inherited('remove', this, arguments);
		},

		setFocus: function(){
			this.$('.vis').first().focus();
		},

		_processVisualizations(visualizations) {
			let markupPayload = [];
			_.each(visualizations, vis => {
				this.viewState.visualizationMap[vis.id || vis.getId()] = vis;
				markupPayload.push({
					id: vis.id || vis.getId(),
					name: vis.caption || vis.getLabel(),
					visId: vis.id || vis.getId(),
					caption: vis.caption || vis.getLabel(),
					iconUri: vis.icon || vis.getIcon()
				});
			});
			return markupPayload;
		},

		/**
		* We want to dynamically load all the SVGs (why load them if we never need them)
		* This method loads the SVG files into a map, asks the widget for a list
		* of recommended and non-recommended visualizations, and adds the 'Auto'
		* choice as well.
		*/
		_preload: function() {
			// Load the file map
			this.viewState.visualizationMap = this.viewState.visualizationMap || {};
			return this._loadSVGFileMap();
		},

		_getAllAvailableVisualizations() {
			let allVisualizations = this.dashboard.getFeature('VisDefinitions').getList();
			let processedItems = this._processVisualizations(allVisualizations);
			this.viewState.all.items = _.sortBy(processedItems, vis => {return vis.name;});
			this.viewState.all.isLoading = false;
		},

		_getRecommendedVisualizations() {
			return this.content.getFeature('Visualization.SmartsRecommender').getRecommendedVisualizations()
				.then(recommended => {
					if(this.viewState === null) {
						return; //Remove has already been called, stop processing
					}
					let processedVisualizations = this._processVisualizations(recommended);
					processedVisualizations.unshift({
						id: AUTO_ID,
						name: resources.get('automaticTypeCaption'),
						visId: AUTO_ID,
						caption: resources.get('automaticTypeCaption'),
						iconUri: 'visualizations-changeVisualization',
						disabled: processedVisualizations.length === 0
					});
					let maxVisualizationsToShow = MaxRecommendedVisualizationsToShow;
					if (this.viewState.selectedId) {
					// Don't include Auto in the limit
						maxVisualizationsToShow++;
					}
					this.viewState.recommended.items = processedVisualizations.slice(0, maxVisualizationsToShow);
					this.viewState.recommended.isLoading = false;
				});
		},

		_initializeViewState() {
			this.viewState = {
				recommended: {
					items: [],
					isLoading: true
				},
				all: {
					items: [],
					isLoading: true
				},
				svgsMap: null,
				selectedId: this._getSelectedVizId(),
				visualizationMap: null,
				iconsFeature: this.iconsFeature,
				onItemChange: this._onItemChange.bind(this)
			};
		},

		//called when an item in the accordian is changed
		_onItemChange() {
			//ensure this flyout view is not off the screen
			var diff = this._getOffScreenHeight();
			if (diff > 0) {
				//TODO: This is not a great solution, especially if we change the way popovers are handled.
				// in the future, any resizing should be handled by the popover class.
				let popover = $(this.el).parents('.popover');
				let top = Math.max(0, popover.position().top - diff);
				popover && popover.css( { top: top } );
			}
		},

		/**
		 * Checks to see if this View is off the visible screen.
		 * @returns the number of pixels this view is off the screen, or -1 if it is not off the screen
		 */
		_getOffScreenHeight() {
			let htmlScrollHeight = this._getDocumentElement().scrollHeight;
			let innerHeight = Utils.getCurrentWindow().innerHeight;
			if (htmlScrollHeight > innerHeight) {
				return htmlScrollHeight - innerHeight;
			}
			return -1;
		},

		/**
		 * @returns The document.documentElement.
		 *  This is here to more easily mock for tests.
		 */
		_getDocumentElement() {
			return document.documentElement;
		},

		_getSelectedVizId() {
			let visualization = this.content.getFeature('Visualization');
			if ((this.currentVis && this.currentVis === AUTO_ID) || (visualization && !visualization.isTypeLocked())) {
				return AUTO_ID;
			} else {
				return visualization.getDefinition().getId();
			}
		},

		_resetRecommendedViewState() {
			this.viewState.recommended = {
				items:[],
				isLoading: true
			};
			if (this._elementExists() && this.reactComponents) {
				ReactDOM.unmountComponentAtNode(this.el);
			}
		},

		/**
		* Create the react components with an initial loading state.
		*/
		_showLoading() {
			this._resetRecommendedViewState();
			// Render the react components
			this.reactComponents = ReactDOM.render(
				<ReactComponents state={this.viewState} />,
				this.el
			);

		},

		/**
		* Render the react components with a state to show the accordions.
		*/
		_showReactSections() {
			this.reactComponents.setState(JSON.parse(JSON.stringify(this.viewState)));
		},

		_render(options) {
			_.extend(this, options || {});
			this._isConsumer();
			this._showLoading();
			return this._preload()
				.then(() => {
					this._showReactSections();
				})
				.then(this._getRecommendedVisualizations.bind(this))
				.then(() => {
					this._showReactSections();
				});
		},

		_isConsumer() {
			const currentContentView = this.dashboard && this.dashboard.getCurrentContentView();
			const glassContext = currentContentView && currentContentView.glassContext;
			if (this.viewState && glassContext) {
				this.viewState.isConsumer = !ErrorUtils.hasCapability(glassContext, 'canAuthorDashboard');
			}
		},

		/**
		* Method to render the view. This includes creating the react components to
		* initially show a progress indicator, preload all the svg icons, divide the
		* choices into sections, then update the react components to show the accordions.
		*/
		render: function (options) {
			this._render(options);
			return Promise.resolve();
		},

		_getTargetFromEvent(event) {
			return $(event.currentTarget);
		},

		// when id auto we highlight auto too
		/**
		 * Event handler for a .vis button is clicked.
		 * Send event to be handled by Live Widget and updates its view with current selection
		 */
		_selectVis: function(event){
			const $currentTarget = this._getTargetFromEvent(event);
			const id = $currentTarget.attr('data-id');
			const recommendedCategory = $currentTarget.attr('data-type');

			// If selected visualization target is same as current visualization target
			if(this.viewState.selectedId === id) {
				return;
			}
			this.viewState.selectedId = id;

			this.reactComponents.setState({ selectedId: id });

			const visDefinition = this.dashboard.getFeature('VisDefinitions').getById(id);
			const visType = visDefinition && visDefinition.getType() || id;

			this.applyAction(visType, { recommendedCategory: recommendedCategory } );

			if (this.selectVisCB) {
				this.selectVisCB();
			}
		}
	});

	return View;

});
